import { createElement } from "../../../UI/UI.ElementarControls/Utils/CustomElementUtils";
import { GlobalContext } from "../GlobalContext";

/** Informace o názvu souboru */
export type FileNameParts = {
    /** Jméno souboru bez přípony */
    nameWithoutExt: string;
    /** Přípona souboru bez tečky */
    extension: string;
    /** Přípona souboru s tečkou */
    extensionWithDot: string | null;
    /** Celý název souboru */
    fullName: string;
};

export class FileUtils {
    /**
     * Z předaného názvu souboru vrátí jeho příponu
     * @description Soubory typu ".htaccess" jsou v tomto případě považovány jako prázdný název s příponou ".htaccess",
     * je možné, že v linuxu nebo macu by to mělo být obráceně, ale s tím si pak zase neporadí naše DMS
     * @author http://stackoverflow.com/a/12900504
     * @param fileName název souboru
     * @param withLeadingDot zda má přípona začínat tečkou
     */
    public static getFileExtension(fileName: string, withLeadingDot: boolean = true): string {
        if (!fileName)
            return "";

        const fileExt = fileName.slice((fileName.lastIndexOf(".") >>> 0) + 1);
        return withLeadingDot && fileExt !== "" ? `.${fileExt}` : fileExt; //`
    }

    /**
     * Regulární výraz obsahující znaky, které nesmí být v názvu souboru
     */
    // eslint-disable-next-line no-control-regex
    private static readonly _INVALID_CHARS = /["<>|:*?/\\\b\t\n\v\f\r\0\u0001\u0002\u0003\u0004\u0005\u0006\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f]/g;

    /**
     * Nahradí podtržítkem znaky, které nesmí být v názvu souboru
     * @param fileName Název souboru k úpravě
     */
    public static replaceInvalidFileNameChars(fileName: string): string {
        return fileName.replace(FileUtils._INVALID_CHARS, "_");
    }

    /**
     * Nabídne soubor uživateli ke stažení
     * @param fileName Název souboru, jaký se má uživateli nabídnout
     * @param file Data souboru
     */
    public static saveFileAs(fileName: string, file: Blob): void {
        const dataUri = window.URL.createObjectURL(file); // díky oneTimeOnly je zbytečné dělat potom rejectObjectURL

        // otevření v top okně kvůli CSP
        const element = createElement({ type: "a", css: { display: "none" } });
        element.setAttribute("href", dataUri);
        element.setAttribute("download", fileName); // díky použití elementu 'a' a tomuto atributu je možné specifikovat, jak se má jmenovat ukládaný soubor
        top?.document.body.appendChild(element);

        element.click();
        top?.document.body.removeChild(element);
    }

    /**
     * Uživatelsky přívětivě naformátuje velikost souboru v bytech
     * @param fileSize Velikost souboru v bytech
     * @param locale Kultura pro formátování čísla
     */
    public static prettyPrintFileSize(fileSize: number, locale?: string | string[]): string {
        const units = ["B", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        let unitsCounter = 0;

        let resultFileSize: number = fileSize;
        let tempFileSize: number;

        for (;;) {
            tempFileSize = resultFileSize;
            tempFileSize = tempFileSize / 1024;
            if (tempFileSize > 1) {
                resultFileSize = tempFileSize;
                ++unitsCounter;

                if (unitsCounter + 1 > units.length)
                    break;
            } else
                break;
        }

        const unitString = (unitsCounter + 1 > units.length) ? "?B" : units[unitsCounter];

        return `${resultFileSize.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${unitString}`; // `
    }

    /**
     * Ořeže příliš dlouhý název tak, aby byly zachovány přípony
     * @param fileName Název daného dokumentu
     * @param [maxLength=255] Maximální délka názvu
     * @returns Ořezaný název dokumentu
     */
    public static trimFileName(fileName: string, maxLength: number = 255): string {
        if (!fileName || fileName.length <= maxLength)
            return fileName;

        //Rozdělit název podle teček
        const partsByDot = fileName.split(".");

        //Oddělit přípony
        const formats = partsByDot.slice(1);
        if (formats.length === 0)
            return fileName.substring(0, 255);

        let index = formats.length - 1;
        let currentSum = 1;
        const acceptedFormats = [];
        const maxNumberOfFormats = 5; //max povolený počet přípon
        const maxLengthOfFormats = 64; //max povolená délka je 64

        //Přípon musí být maximálně 5 a jejich maximální délka (včetně teček) nesmí být větší než 64
        while (acceptedFormats.length < maxNumberOfFormats && currentSum + formats[index].length <= maxLengthOfFormats) {
            acceptedFormats.push(formats[index]);
            currentSum += formats[index].length + 1;
            index--;

            if (index < 0)
                break;
        }

        //Kontrola přípon probíhá odzadu, je potřeba přípony vrátit do původního pořadí
        acceptedFormats.reverse();

        //Přípony
        const formattedFormats = "." + acceptedFormats.join(".");

        //Ořezaný název (případně i přípony, které se nevešly do podmínky (celková délka <64, celkový počet <=5))
        const formattedName = fileName.substring(0, maxLength - formattedFormats.length);

        return formattedName.concat(formattedFormats);
    }

    /**
    * Název souboru rozdělí na název a příponu
    * @param fileName název souboru k rozdělení
    */
    public static splitFileName(fileName: string): FileNameParts {
        if (!fileName || fileName.indexOf(".") === -1) {
            return {
                fullName: fileName ?? "",
                nameWithoutExt: fileName ?? "",
                extension: "",
                extensionWithDot: "",
            };
        }
        const fileParts = fileName.split(".");
        const extension = fileParts.pop() ?? "";
        const nameWithoutExt = fileParts.join(".");
        return {
            fullName: fileName,
            nameWithoutExt,
            extension,
            extensionWithDot: extension ? `.${extension}` : null,
        };
    }
}

GlobalContext.registerType(FileUtils);
